// "Test Configuration" — one per provider, per mode.
//
// WHY IT MOVED HERE
//
// There used to be a single Test Connection button under Going Live. It
// tested whichever provider was ACTIVE, always against its TEST
// credentials. Two things that could not be done from there:
//
//   * verify a provider you have configured but not yet made active —
//     you had to switch to it first, which is the wrong order
//   * verify LIVE credentials at all, which is the one check worth
//     having before a buyer depends on them
//
// Sitting inside each credential box, it tests exactly the credentials
// shown above it, which is what somebody filling that box in wants to
// know.
//
// TESTING LIVE REACHES THE LIVE GATEWAY. It creates a payment job that is
// never paid — no money moves — but it is a real record on the real
// account. The backend requires Live mode for it, the same rule that
// governs editing live credentials, and the button says so before it is
// pressed.
import React, { useState } from "react";

export default function TestConfigurationBox({
  token,
  wsRequest,
  provider,
  mode,
  action = "admin.paymentProvider.testConnection",
  disabled = false,
  disabledReason = null,
  hasCredentials = false,
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const data = await wsRequest(action, { provider, mode }, token);
      setResult({ ok: true, data });
    } catch (err) {
      setResult({ ok: false, message: err.message || "Could not reach backend." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        marginTop: 10,
        paddingTop: 10,
        borderTop: "1px solid var(--border-color)",
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={run}
          disabled={busy || disabled || !hasCredentials}
          title={
            disabledReason ||
            (!hasCredentials ? "Save credentials first" : undefined)
          }
        >
          {busy ? "Testing…" : "Test configuration"}
        </button>
                  {/* WHY IT IS DISABLED, VISIBLY.
              The reason was only in the button's title attribute — a hover
              tooltip, which nobody thinks to try on a disabled control.
              What was visible beside it described what the button DOES,
              and read identically whether it worked or not, so an
              unconfigured provider looked broken. */}
          <span style={{ fontSize: 12, opacity: 0.7 }}>
            {!hasCredentials
              ? "Save credentials above before you can test them."
              : disabledReason
                ? disabledReason
                : mode === "live"
                  ? "Reaches the live gateway. Creates an unpaid payment job — no money moves."
                  : "Creates a test payment job against the sandbox."}
          </span>
      </div>

      {result && (
        <div
          style={{
            marginTop: 8,
            padding: "8px 10px",
            borderRadius: 6,
            fontSize: 12.5,
            border:
              "1px solid " + (result.ok ? "var(--accent-green, #5ed46a)" : "var(--accent-red)"),
          }}
        >
          {result.ok ? (
            <>
              <strong style={{ color: "var(--accent-green, #5ed46a)" }}>
                These credentials work.
              </strong>{" "}
              {result.data?.ms ? `Answered in ${result.data.ms}ms.` : null}
              {/* The provider's own reference, because it is the one thing
                  proving a checkout was really created rather than the
                  request merely being accepted — and it is what an
                  operator quotes if they have to ask the provider about
                  it. */}
              {result.data?.providerReference && (
                <div style={{ opacity: 0.7, marginTop: 4, fontFamily: "monospace" }}>
                  reference {result.data.providerReference}
                </div>
              )}
            </>
          ) : (
            <>
              <strong style={{ color: "var(--accent-red)" }}>Failed.</strong> {result.message}
            </>
          )}
        </div>
      )}
    </div>
  );
}
